import { useTranslation } from 'react-i18next'
import { Select as AntSelect, Spin } from 'antd'
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers'
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useState, useMemo, useEffect } from 'react'
import EmptyState from 'components/reusables/emptyState'
import Select from 'components/reusables/select'
import { AvatarWithInfo } from 'components/reusables/avatarWithInfo/AvatarWithInfo'
import { ISimpleUser } from 'interfaces/users'
import { usersService } from 'api/configuration/users'
import { Wrapper, Subtitle, OptionWrapper } from './styled'

const { Option } = AntSelect

interface IProps {
  selectedUsers: ISimpleUser[]
  setSelectedUsers: (users: ISimpleUser[]) => void
  disabled: boolean
}

const SelectUsers = ({ selectedUsers, setSelectedUsers, disabled }: IProps) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [users, setUsers] = useState<ISimpleUser[]>([])
  const [loading, setLoading] = useState(false)
  const prefix =
    'configuration.rolesAndPermissions.assignRole.inputs.chooseUsers'

  useEffect(() => {
    const criteria = searchText.trim()
    const debounceMs = criteria ? 800 : 0

    const delayTimer = setTimeout(() => {
      setLoading(true)
      usersService
        .getUnassignedUsers(criteria)
        .then((response) => {
          setUsers(response.data ?? [])
        })
        .finally(() => {
          setLoading(false)
        })
    }, debounceMs)

    return () => {
      clearTimeout(delayTimer)
    }
  }, [searchText])

  const displayedUsers = useMemo(() => {
    const selectedUserIds = selectedUsers.map((user) => user.id)
    const filteredUsers = users.filter(
      (user) => !selectedUserIds.includes(user.id)
    )
    return [...filteredUsers, ...selectedUsers]
  }, [users, selectedUsers])

  return (
    <Wrapper $isEmpty={selectedUsers.length === 0}>
      <Subtitle>{t(`${prefix}.title`)}</Subtitle>
      <Spin spinning={loading}>
        <Select
          showSearch={true}
          mode="multiple"
          value={selectedUsers.map((user) => user.id)}
          placeholder={t(`${prefix}.placeholder`)}
          onSearch={(value) => setSearchText(value)}
          filterOption={false}
          onSelect={(value) =>
            setSelectedUsers([
              ...selectedUsers,
              users.find((user) => user.id === value) as ISimpleUser,
            ])
          }
          onDeselect={(value) =>
            setSelectedUsers(selectedUsers.filter((user) => user.id !== value))
          }
          notFoundContent={
            <EmptyState title={t(`${prefix}.emptyState`)} icon={faUsers} />
          }
          disabled={disabled}
        >
          {displayedUsers.map((item) => (
            <Option
              key={item.id}
              value={item.id}
              label={`${item.firstName} ${item.lastName}`}
            >
              <OptionWrapper>
                <AvatarWithInfo
                  image={item.avatar}
                  icon={faUser}
                  background="#0085FF"
                  size={24}
                  borderRadius={50}
                  title={`${item.firstName} ${item.lastName}`}
                  subtitle={item.identifier}
                  principalColor="#0E0E0E"
                  fontWeight="normal"
                />
                <FontAwesomeIcon
                  icon={faTimes as IconProp}
                  onClick={() =>
                    setSelectedUsers(
                      selectedUsers.filter((user) => user.id !== item.id)
                    )
                  }
                />
              </OptionWrapper>
            </Option>
          ))}
        </Select>
      </Spin>
    </Wrapper>
  )
}

export default SelectUsers
